import { useHeaderHeight } from 'hooks/useHeaderHeight/useHeaderHeight'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useEffect, useRef, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

import { useColors } from '../../../contexts/ColorsProvider/ColorsProvider'
import type { Look, Media } from '../../../types/FullProduct'
import { PDP_PRIMARY_ACTIONS } from '../../PrimaryActions/PrimaryActions'

const TOP_PAGE = 0
const LABEL_COLOR = 'accessibility.pdp.addColor.selector.withColor'
const STICKY_PADDING = 12
const DEBOUNCE_TIME = 300

type UseSticky = {
	show: boolean
	image: Media
	colorAlt: string
	scrollToTop: () => void
}

export function useSticky(look: Look): UseSticky {
	const [show, setShow] = useState(false)
	const canToggle = useRef<boolean>(true)
	const formRef = useRef<HTMLElement | null>(null)
	const { headerHeight } = useHeaderHeight()
	const stickyHeight =
		(formRef.current?.getBoundingClientRect().height || 0) + STICKY_PADDING
	const rootMarginTop = headerHeight + (!show ? stickyHeight : 0)
	const entry = useIntersectionObserver(formRef, {
		threshold: 0.01,
		rootMargin: `${-rootMarginTop}px 0px 0px 0px`,
	})
	const { isLargeOrGreater } = useResponsive()

	const { t } = useLabels()
	const {
		selectedColor: { label },
	} = useColors()

	const colorAlt = replacePlaceholders(t(LABEL_COLOR), { 1: label })
	const firstImage =
		look.media.find((media) => media.format === 'IMAGE') || look.media[0]

	const scrollToTop = () => {
		window.scrollTo({ top: TOP_PAGE, behavior: 'smooth' })
	}

	useEffect(() => {
		formRef.current = document.getElementById(PDP_PRIMARY_ACTIONS)
	}, [])

	useEffect(() => {
		// Prevents flickering when the user scrolls slowly
		canToggle.current = false
		setTimeout(() => {
			canToggle.current = true
		}, DEBOUNCE_TIME)
	}, [show])

	useEffect(() => {
		const shouldShow =
			isLargeOrGreater && Boolean(entry) && !entry?.isIntersecting
		if (canToggle.current && shouldShow && !show) {
			setShow(true)
		}
		if (canToggle.current && !shouldShow && show) {
			setShow(false)
		}
	}, [isLargeOrGreater, entry])

	return { image: firstImage, show, scrollToTop, colorAlt }
}
